import { Box, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";

import { useCurrentUser } from "@/auth/RequireAuth";

import { PageHeader } from "@/components/Common/PageHeader";
import { EmbedKeyList } from "@/components/EmbedKeys/EmbedKeyList";
import { CompanyMembersPage } from "@/routes/workspace/CompanyMembers";
import { DepartmentsPage } from "@/routes/workspace/Departments";
import { EventAnalysis } from "@/routes/workspace/EventAnalysis";
import { KbSettings } from "@/routes/workspace/KbSettings";
import { BudgetSettings } from "@/routes/workspace/BudgetSettings";
import { LlmSettings } from "@/routes/workspace/LlmSettings";
import { AgentEmailSettings } from "@/routes/workspace/AgentEmailSettings";
import { PolicySettings } from "@/routes/workspace/PolicySettings";
import { RolesAdmin } from "@/routes/workspace/RolesAdmin";

type SectionId =
  | "members"
  | "departments"
  | "roles"
  | "embed-keys"
  | "kb-settings"
  | "ai-model"
  | "spend-guard"
  | "content-policies"
  | "agent-email"
  | "event-analysis";

interface NavGroup {
  label: string;
  items: { id: SectionId; label: string }[];
  /** Hidden from everyone but platform admins. */
  platformAdminOnly?: boolean;
}

// Semantic groups mirror the Claude console — "People" bundles identity /
// access-control surfaces; "Access" bundles machine-auth surfaces. Add new
// groups here rather than inflating one flat list.
const NAV: NavGroup[] = [
  {
    label: "People",
    items: [
      { id: "members", label: "Members" },
      { id: "departments", label: "Departments" },
      { id: "roles", label: "Roles" },
    ],
  },
  {
    label: "Access",
    items: [{ id: "embed-keys", label: "Embed keys" }],
  },
  {
    label: "Knowledge",
    items: [{ id: "kb-settings", label: "KB settings" }],
  },
  {
    label: "Analytics",
    // PLATFORM ADMIN ONLY. `/posthog/v1/reports/latest` serves the weekly
    // report without scoping it to the caller's company — the endpoint takes
    // its principal as `_principal` and never filters on it, so whoever can
    // reach it sees every tenant's numbers. The service itself now enforces
    // this (`_is_platform_admin`, posthog_service/app.py) and refuses a
    // company admin with 403; this flag is the UI half of the same rule, so
    // the tab does not even appear for someone the API would then reject.
    platformAdminOnly: true,
    items: [{ id: "event-analysis", label: "Event data analysis" }],
  },
  {
    label: "AI",
    items: [
      { id: "ai-model", label: "AI model" },
      // Next to the AI model settings because it is the same kind of decision
      // -- what the agent may do on this org's behalf -- and the only one
      // whose effect leaves the product. Off until an admin turns it on.
      { id: "agent-email", label: "Agent email" },
      { id: "spend-guard", label: "Spend guard" },
      { id: "content-policies", label: "Content policies" },
    ],
  },
];

const TITLES: Record<SectionId, string> = {
  members: "Members",
  departments: "Departments",
  roles: "Roles",
  "embed-keys": "Embed keys",
  "kb-settings": "KB settings",
  "ai-model": "AI model",
  "spend-guard": "Spend guard",
  "content-policies": "Content policies",
  "agent-email": "Agent email",
  "event-analysis": "Event data analysis",
};

/** Organization settings — Claude-style two-pane layout. Left sub-sidebar
 *  routes between the org-scoped admin surfaces (Members, Roles, Embed keys);
 *  main pane renders the active section. Adding a new section = one entry in
 *  `NAV` + one branch in the render switch. */
export function OrganizationSettings() {
  const [section, setSection] = useState<SectionId>("members");
  const user = useCurrentUser();
  // `undefined` until the user resolves — treated as NOT a platform admin, so
  // a gated tab never flashes into view while the answer is in flight.
  const isPlatformAdmin = user?.is_platform_admin === true;

  const nav = useMemo(
    () => NAV.filter((g) => !g.platformAdminOnly || isPlatformAdmin),
    [isPlatformAdmin],
  );
  const visible = useMemo(
    () => new Set(nav.flatMap((g) => g.items.map((i) => i.id))),
    [nav],
  );

  return (
    <>
      <PageHeader title="Organization settings" />
      <Box sx={{ display: "flex", gap: 4, alignItems: "flex-start" }}>
        <Box
          component="nav"
          sx={{
            width: 220,
            flexShrink: 0,
            position: "sticky",
            top: 0,
            alignSelf: "flex-start",
          }}
          aria-label="Organization settings sections"
          data-ph-scope="nav"
        >
          <Stack spacing={3}>
            {nav.map((group) => (
              <Stack key={group.label} spacing={0.5}>
                <Typography
                  variant="caption"
                  sx={{
                    px: 1,
                    color: "text.secondary",
                    fontWeight: 600,
                    letterSpacing: 0.4,
                  }}
                >
                  {group.label}
                </Typography>
                {group.items.map((item) => {
                  const active = section === item.id;
                  return (
                    <Box
                      key={item.id}
                      component="button"
                      // A literal per section (T5): the tag names the tab it
                      // opens, matching that tab's own section scope.
                      data-ph={
                        item.id === "members" ? "open_members"
                        : item.id === "departments" ? "open_departments"
                        : item.id === "roles" ? "open_roles"
                        : item.id === "embed-keys" ? "open_embed_keys"
                        : item.id === "kb-settings" ? "open_kb_settings"
                        : item.id === "ai-model" ? "open_ai_model"
                        : item.id === "spend-guard" ? "open_spend_guard"
                        : item.id === "content-policies" ? "open_content_policies"
                        : item.id === "agent-email" ? "open_agent_email"
                        : "open_event_analysis"
                      }
                      onClick={() => setSection(item.id)}
                      aria-current={active ? "page" : undefined}
                      sx={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        border: 0,
                        cursor: "pointer",
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 1.5,
                        fontSize: 14,
                        fontFamily: "inherit",
                        color: active ? "text.primary" : "text.secondary",
                        fontWeight: active ? 600 : 500,
                        bgcolor: active ? "action.selected" : "transparent",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      {item.label}
                    </Box>
                  );
                })}
              </Stack>
            ))}
          </Stack>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }} role="region" aria-label={TITLES[section]}>
          {section === "members" && <CompanyMembersPage embedded />}
          {section === "departments" && <DepartmentsPage embedded />}
          {section === "roles" && <RolesAdmin />}
          {section === "embed-keys" && <EmbedKeyList embedded />}
          {section === "kb-settings" && <KbSettings />}
          {section === "event-analysis" && visible.has("event-analysis") && (
            <EventAnalysis />
          )}
          {section === "ai-model" && <LlmSettings />}
          {section === "spend-guard" && <BudgetSettings />}
          {section === "content-policies" && <PolicySettings />}
          {section === "agent-email" && <AgentEmailSettings />}
        </Box>
      </Box>
    </>
  );
}
